import dayjs from 'dayjs';
import _capitalize from 'lodash/capitalize';
import { Alert, Button, Space, Tag, Tooltip } from 'antd';
import { DeleteOutlined, EditOutlined, UndoOutlined } from '@ant-design/icons';

import { PAYMENT_STATES } from 'utilities/constants';
import { formatDollarsFromCents } from 'utilities/helpers';

import { OfferPaymentsTable, applyRowClass } from '../OfferPaymentsTable';
import {
  togglePrecoPaymentDeleted,
  undoPrecoPaymentEdit,
} from './precoPaymentUtils';

const STATUS_FILTERS = [
  { text: 'Posted', value: PAYMENT_STATES.POSTED },
  { text: 'Pending', value: PAYMENT_STATES.PENDING },
  { text: 'Returned', value: PAYMENT_STATES.RETURNED },
  { text: 'Scheduled', value: PAYMENT_STATES.SCHEDULED },
  { text: 'Executed', value: PAYMENT_STATES.EXECUTED },
];

const STATUS_TAG = {
  add: <Tag color="purple">New</Tag>,
  edit: <Tag color="orange">Modified</Tag>,
  delete: <Tag color="red">Removed</Tag>,
};

// Past payments carry `type`, plan transactions carry `status`, and rows the
// agent is still drafting have neither.
const getPaymentState = (payment) => payment.type || payment.status || '';

const getColumns = ({
  onEditPayment,
  onDeletePayment,
  onUndoEdit,
  showStatus,
}) =>
  [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix(),
      render: (date) => dayjs(date).format('LL'),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      sorter: (a, b) => a.amount - b.amount,
      render: (amount) => formatDollarsFromCents(amount),
    },
    showStatus && {
      title: 'Status',
      dataIndex: '',
      key: 'status',
      width: 150,
      filters: STATUS_FILTERS,
      filterMode: 'tree',
      onFilter: (value, record) => getPaymentState(record) === value,
      sorter: (a, b) => getPaymentState(a).localeCompare(getPaymentState(b)),
      render: (_, record) =>
        STATUS_TAG[record.changeType] || _capitalize(getPaymentState(record)),
    },
    onEditPayment && {
      title: '',
      key: 'actions',
      width: 72,
      render: (_, record) => {
        if (!record.editable || record.type) {
          return null;
        }

        const isDeleted = record.changeType === 'delete';
        const isEdited = record.changeType === 'edit';

        return (
          <Space.Compact>
            {!isDeleted && !isEdited && (
              <Tooltip title="Edit">
                <Button
                  icon={<EditOutlined style={{ color: 'var(--blue-4)' }} />}
                  onClick={() => onEditPayment(record)}
                />
              </Tooltip>
            )}
            <Tooltip title={isDeleted || isEdited ? 'Undo' : 'Delete'}>
              <Button
                icon={
                  isDeleted || isEdited ? (
                    <UndoOutlined style={{ color: 'var(--blue-4)' }} />
                  ) : (
                    <DeleteOutlined style={{ color: 'var(--red-3)' }} />
                  )
                }
                onClick={() =>
                  isEdited ? onUndoEdit(record) : onDeletePayment(record)
                }
              />
            </Tooltip>
          </Space.Compact>
        );
      },
    },
  ].filter(Boolean);

const PrecoScheduleTable = ({
  data,
  schedule,
  setSchedule,
  onEditPayment,
  balanceAlert,
  showStatus = true,
  length = 7,
}) => {
  const onDeletePayment = (record) =>
    setSchedule(togglePrecoPaymentDeleted(schedule, record.date));

  const onUndoEdit = (record) =>
    setSchedule(undoPrecoPaymentEdit(schedule, record.date));

  return (
    <>
      <OfferPaymentsTable
        rowClassName={onEditPayment ? applyRowClass : undefined}
        columns={getColumns({
          onEditPayment,
          onDeletePayment,
          onUndoEdit,
          showStatus,
        })}
        data={data}
        length={length}
        style={{ marginBottom: 'var(--spacing-sm)' }}
        showCopyButton={false}
      />
      {onEditPayment &&
        (balanceAlert ? (
          <Alert
            banner
            type={balanceAlert.type}
            title={balanceAlert.message}
            style={{
              marginTop: 'var(--spacing-md)',
              marginBottom: 'var(--spacing-md)',
            }}
          />
        ) : (
          <Alert
            banner
            showIcon
            type="info"
            title="Customers can always edit their scheduled payments as long as they stay within the plan's due date and as long as the amounts total to the remaining balance."
            style={{
              marginTop: 'var(--spacing-sm)',
              marginBottom: 'var(--spacing-sm)',
            }}
          />
        ))}
    </>
  );
};

export default PrecoScheduleTable;
